import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Minus, TrendingUp, TrendingDown } from 'lucide-react';
import { adminAPI, getErrorMessage } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ASSET_NETWORKS = {
  BTC: 'testnet3',
  ETH: 'sepolia',
  USDT: 'sepolia',
  USDC: 'sepolia',
  BNB: 'bsc-testnet',
  SOL: 'devnet',
  XRP: 'xrp-testnet',
  ADA: 'preprod',
  DOGE: 'testnet',
  LTC: 'testnet',
  MATIC: 'amoy',
  GBP: 'fiat',
  USD: 'fiat',
};

const SUPPORTED_ASSETS = [
  { symbol: 'GBP',  name: 'British Pound', isFiat: true  },
  { symbol: 'USD',  name: 'US Dollar',     isFiat: true  },
  { symbol: 'BTC',  name: 'Bitcoin'                      },
  { symbol: 'ETH',  name: 'Ethereum'                     },
  { symbol: 'USDT', name: 'Tether'                       },
  { symbol: 'USDC', name: 'USD Coin'                     },
  { symbol: 'BNB',  name: 'BNB'                          },
  { symbol: 'SOL',  name: 'Solana'                       },
  { symbol: 'XRP',  name: 'XRP'                          },
  { symbol: 'ADA',  name: 'Cardano'                      },
  { symbol: 'DOGE', name: 'Dogecoin'                     },
  { symbol: 'LTC',  name: 'Litecoin'                     },
  { symbol: 'MATIC', name: 'Polygon'                     },
];

const FIAT_SYMBOLS = new Set(['GBP', 'USD']);

const ASSET_NAMES = Object.fromEntries(SUPPORTED_ASSETS.map((a) => [a.symbol, a.name]));

function formatBalance(amount, symbol) {
  if (FIAT_SYMBOLS.has(symbol)) {
    const prefix = symbol === 'GBP' ? '£' : '$';
    return `${prefix}${Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const n = Number(amount);
  if (n % 1 === 0) return n.toFixed(2);
  return parseFloat(n.toFixed(8)).toString();
}

const AdminUserWallets = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editAddress, setEditAddress] = useState('');
  const [saving, setSaving] = useState(false);

  // Balance adjustment modal state
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [adjReference, setAdjReference] = useState('');
  const [adjLoading, setAdjLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, walletsRes, balancesRes] = await Promise.all([
        adminAPI.getUser(userId),
        adminAPI.getUserWallets(userId),
        adminAPI.getUserBalances(userId),
      ]);
      setUser(userRes.data.user);
      setWallets(walletsRes.data.wallets || []);
      setBalances(balancesRes.data.balances || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const refreshBalances = useCallback(async () => {
    try {
      const res = await adminAPI.getUserBalances(userId);
      setBalances(res.data.balances || []);
    } catch {
      // silent — table will just stay stale
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const walletBySymbol = Object.fromEntries(wallets.map((w) => [w.asset_symbol, w]));
  const balanceBySymbol = Object.fromEntries(balances.map((b) => [b.asset_symbol, b]));

  const startEdit = (asset) => {
    const existing = walletBySymbol[asset.symbol];
    setEditingSymbol(asset.symbol);
    setEditAddress(existing?.wallet_address || '');
  };

  const cancelEdit = () => {
    setEditingSymbol(null);
    setEditAddress('');
  };

  const saveWallet = async (asset) => {
    if (!editAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.assignWallet(userId, {
        asset_symbol: asset.symbol,
        wallet_address: editAddress.trim(),
        network: ASSET_NETWORKS[asset.symbol],
      });
      toast.success(`${asset.symbol} wallet assigned`);
      setEditingSymbol(null);
      setEditAddress('');
      adminAPI.getUserWallets(userId).then((res) => setWallets(res.data.wallets || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign wallet');
    } finally {
      setSaving(false);
    }
  };

  const openAdjustModal = (balance, action) => {
    setAdjAmount('');
    setAdjReason('');
    setAdjReference('');
    setAdjustModal({ asset_symbol: balance.asset_symbol, action, current_balance: parseFloat(balance.available_balance) || 0 });
  };

  const closeAdjustModal = () => {
    setAdjustModal(null);
    setAdjAmount('');
    setAdjReason('');
    setAdjReference('');
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!adjustModal) return;
    setAdjLoading(true);
    try {
      await adminAPI.adjustBalance(userId, {
        asset_symbol: adjustModal.asset_symbol,
        amount: parseFloat(adjAmount),
        action: adjustModal.action,
        reason: adjReason,
        reference: adjReference,
      });
      const verb = adjustModal.action === 'credit' ? 'added' : 'removed';
      toast.success(`${adjAmount} ${adjustModal.asset_symbol} ${verb} successfully`);
      closeAdjustModal();
      refreshBalances();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAdjLoading(false);
    }
  };

  // Derived values for modal preview
  const adjAmountNum = parseFloat(adjAmount) || 0;
  const modalNewBalance = adjustModal
    ? adjustModal.action === 'credit'
      ? adjustModal.current_balance + adjAmountNum
      : adjustModal.current_balance - adjAmountNum
    : 0;
  const adjExceedsBalance =
    adjustModal?.action === 'debit' && adjAmountNum > adjustModal?.current_balance;
  const adjCanSubmit =
    adjAmountNum > 0 && adjReason.trim() !== '' && !adjExceedsBalance;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Users
      </button>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          Wallet Assignment
          {user && (
            <span className="text-muted-foreground font-normal">
              {' — '}{user.full_name} ({user.email})
            </span>
          )}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Assign blockchain wallet addresses for this user
        </p>
      </div>

      <Card className="glass-card p-0 overflow-hidden">
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Network</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Wallet Address</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Balance</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Available</th>
                <th className="text-center text-muted-foreground text-sm font-medium p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORTED_ASSETS.map((asset) => {
                const w = walletBySymbol[asset.symbol];
                const isEditing = editingSymbol === asset.symbol;
                const hasAddress = w?.wallet_address;
                const network = ASSET_NETWORKS[asset.symbol];

                return (
                  <React.Fragment key={asset.symbol}>
                    <tr className="table-row border-b border-border last:border-0">
                      <td className="p-4">
                        <div className="font-semibold text-foreground">{asset.symbol}</div>
                        <div className="text-xs text-muted-foreground">{asset.name}</div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground font-mono">{network}</span>
                      </td>
                      <td className="p-4 max-w-xs">
                        {hasAddress ? (
                          <span className="text-sm font-mono text-foreground break-all">
                            {w.wallet_address}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-red-500">Not assigned</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm text-foreground">
                        {w ? w.balance.toFixed(8) : '—'}
                      </td>
                      <td className="p-4 text-right text-sm text-foreground">
                        {w ? w.available_balance.toFixed(8) : '—'}
                      </td>
                      <td className="p-4 text-center">
                        {!isEditing && (
                          <Button
                            size="sm"
                            variant={hasAddress ? 'outline' : 'default'}
                            onClick={() => startEdit(asset)}
                          >
                            {hasAddress ? 'Edit' : 'Assign'}
                          </Button>
                        )}
                        {isEditing && (
                          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving}>
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-muted/30 border-b border-border">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-end gap-3 flex-wrap">
                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                              <label className="text-xs text-muted-foreground font-medium">
                                {asset.symbol} Wallet Address
                              </label>
                              <input
                                className="input-field text-sm font-mono"
                                value={editAddress}
                                onChange={(e) => setEditAddress(e.target.value)}
                                placeholder={`Enter ${asset.symbol} wallet address...`}
                                autoFocus
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs text-muted-foreground font-medium">Network</label>
                              <div className="px-3 py-2 text-sm font-mono text-muted-foreground bg-muted rounded-md border border-border">
                                {network}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => saveWallet(asset)} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Balance Management ── */}
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Balance Management
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            Manually credit or debit user balances. All changes are audit logged.
          </p>
        </div>
      </div>

      <Card className="glass-card p-0 overflow-hidden">
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Asset</th>
                <th className="text-left text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Network</th>
                <th className="text-left text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Wallet Status</th>
                <th className="text-right text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Available Balance</th>
                <th className="text-right text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Total Balance</th>
                <th className="text-center text-gray-500 dark:text-gray-400 text-sm font-medium p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORTED_ASSETS.map((asset) => {
                const bal = balanceBySymbol[asset.symbol];
                const availBal = parseFloat(bal?.available_balance) || 0;
                const totalBal = parseFloat(bal?.total_balance) || 0;
                const hasAddress = bal?.has_address || walletBySymbol[asset.symbol]?.wallet_address;
                return (
                  <tr key={asset.symbol} className="table-row border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <td className="p-4">
                      <div className="font-semibold text-gray-900 dark:text-white">{asset.symbol}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{asset.name}</div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full font-mono">
                        {ASSET_NETWORKS[asset.symbol] || '—'}
                      </span>
                    </td>
                    <td className="p-4">
                      {hasAddress ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full font-medium">
                          Assigned
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full font-medium">
                          Not Assigned
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right text-sm text-gray-900 dark:text-white font-mono">
                      {formatBalance(availBal, asset.symbol)}
                    </td>
                    <td className="p-4 text-right text-sm text-gray-900 dark:text-white font-mono">
                      {formatBalance(totalBal, asset.symbol)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openAdjustModal({ asset_symbol: asset.symbol, available_balance: availBal }, 'credit')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          <Plus size={12} />
                          Add Funds
                        </button>
                        <button
                          onClick={() => openAdjustModal({ asset_symbol: asset.symbol, available_balance: availBal }, 'debit')}
                          disabled={availBal === 0}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Minus size={12} />
                          Remove Funds
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Balance Adjustment Modal ── */}
      <Dialog open={!!adjustModal} onOpenChange={(open) => { if (!open) closeAdjustModal(); }}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {adjustModal?.action === 'credit' ? (
                <TrendingUp size={18} className="text-green-500" />
              ) : (
                <TrendingDown size={18} className="text-red-500" />
              )}
              {adjustModal?.action === 'credit'
                ? `Add Funds — ${adjustModal?.asset_symbol}`
                : `Remove Funds — ${adjustModal?.asset_symbol}`}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAdjust} className="space-y-4 pt-1">
            {/* Current balance */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Current Balance</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {adjustModal
                  ? `${formatBalance(adjustModal.current_balance, adjustModal.asset_symbol)} ${adjustModal.asset_symbol}`
                  : '—'}
              </p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount ({adjustModal?.asset_symbol})
              </label>
              <input
                type="number"
                min="0.00000001"
                step="any"
                placeholder="Enter amount"
                value={adjAmount}
                onChange={(e) => setAdjAmount(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {adjAmountNum > 0 && adjustModal && (
                <p className={`text-xs mt-1.5 font-medium ${adjustModal.action === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  New balance will be: {formatBalance(modalNewBalance, adjustModal.asset_symbol)} {adjustModal.asset_symbol}
                </p>
              )}
              {adjExceedsBalance && (
                <p className="text-xs mt-1 text-red-600 dark:text-red-400 font-medium">
                  ⚠ Exceeds available balance
                </p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="e.g. Manual deposit correction, Bonus credit, Fee refund..."
                rows={2}
                value={adjReason}
                onChange={(e) => setAdjReason(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reference <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. TXN-12345"
                value={adjReference}
                onChange={(e) => setAdjReference(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={closeAdjustModal}
                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!adjCanSubmit || adjLoading}
                className={`flex-1 py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  adjustModal?.action === 'credit'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {adjLoading
                  ? 'Processing...'
                  : adjustModal?.action === 'credit'
                  ? `Add ${adjAmount || '0'} ${adjustModal?.asset_symbol}`
                  : `Remove ${adjAmount || '0'} ${adjustModal?.asset_symbol}`}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserWallets;
